// hooks/useStrategyWebSocket.js - PRODUCTION READY
import { useState, useEffect, useRef, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECT_ATTEMPTS = 5;

export const useStrategyWebSocket = (apiBaseUrl, strategyId, onUpdate) => {
  const [isConnected, setIsConnected] = useState(false);
  const [currentState, setCurrentState] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const mountedRef = useRef(true);
  const onUpdateRef = useRef(onUpdate);
  const connectionStartTime = useRef(null);

  // Keep callback ref updated
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  const connect = useCallback(() => {
    if (!strategyId || !mountedRef.current) {
      return;
    }

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      connectionStartTime.current = performance.now();

      // ✅ FIX: Always derive WebSocket host from apiBaseUrl, not window.location
      // This ensures WebSocket connects to the correct backend server regardless of
      // what port the frontend is served from
      
      // Parse the apiBaseUrl to get host and port
      let wsHost;
      try {
        const apiUrl = new URL(apiBaseUrl);
        wsHost = apiUrl.host; // This includes port if specified
      } catch (e) {
        // Fallback: strip protocol manually
        wsHost = apiBaseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      }

      // Determine WebSocket protocol based on apiBaseUrl protocol
      const isSecure = apiBaseUrl.startsWith('https://');
      const protocol = isSecure ? 'wss:' : 'ws:';

      const wsUrl = `${protocol}//${wsHost}/ws/strategies/monitor/${strategyId}/`;
      console.log(`[WS-${strategyId}-PERF] ⏱️ Connecting to: ${wsUrl}`);

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!mountedRef.current) return;

        const connectionTime = performance.now() - connectionStartTime.current;
        console.log(`[WS-${strategyId}-PERF] ✅ Connected in ${connectionTime.toFixed(2)}ms`);

        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;

        const messageStartTime = performance.now();

        try {
          const data = JSON.parse(event.data);
          const parseTime = performance.now() - messageStartTime;

          if (data.type === 'connection') {
            console.log(`[WS-${strategyId}-PERF] Connection confirmed`);
            return;
          }

          // Handle strategy_state_update from broadcaster
          if (data.type === 'update' && data.data) {
            console.log(`[WS-${strategyId}-PERF] 📨 Update received in ${parseTime.toFixed(2)}ms`);

            const stateData = data.data;

            // Normalize the state data
            const normalizedState = {
              status: stateData.status,
              completion_percentage: stateData.completion_percentage,
              entry_time: stateData.entry_time,
              entry_price: stateData.entry_price,
              exit_time: stateData.exit_time,
              exit_price: stateData.exit_price,
              pnl: stateData.pnl,
              pnl_pct: stateData.pnl_pct,
              trigger_signal: stateData.trigger_signal,
              confirmations: stateData.confirmations,
              pending_confirmations: stateData.pending_confirmations,
              required_signal: stateData.required_signal,
              summary: stateData.summary,
              timestamp: stateData.timestamp || data.timestamp,
            };

            setCurrentState(normalizedState);

            if (onUpdateRef.current) {
              onUpdateRef.current(normalizedState);
            }
          }

          // Handle legacy state format
          if (data.type === 'state' && data.current_state) {
            console.log(`[WS-${strategyId}-PERF] 📊 State received in ${parseTime.toFixed(2)}ms`);

            setCurrentState(data.current_state);

            if (onUpdateRef.current) {
              onUpdateRef.current(data.current_state);
            }
          }
        } catch (err) {
          console.error(`[WS-${strategyId}] Parse error:`, err);
        }
      };

      ws.onerror = (error) => {
        console.error(`[WS-${strategyId}] Error:`, error);
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;

        console.log(`[WS-${strategyId}] Disconnected (code: ${event.code})`);
        setIsConnected(false);

        // Attempt reconnect for abnormal closures
        if (event.code !== 1000 && reconnectAttemptsRef.current < WS_MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current++;

          console.log(`[WS-${strategyId}] Reconnecting... (attempt ${reconnectAttemptsRef.current}/${WS_MAX_RECONNECT_ATTEMPTS})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
              connect();
            }
          }, WS_RECONNECT_DELAY);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error(`[WS-${strategyId}] Connection error:`, err);
    }
  }, [apiBaseUrl, strategyId]);

  // Connect/disconnect when strategyId changes
  useEffect(() => {
    mountedRef.current = true;

    if (strategyId) {
      connect();
    } else {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setIsConnected(false);
      setCurrentState(null);
    }

    return () => {
      mountedRef.current = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, [connect, strategyId]);

  // Force reconnect
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  return {
    isConnected,
    currentState,
    reconnect,
  };
};

export default useStrategyWebSocket;
